// src/shared/components/ServiceUnavailable.tsx
// ────────────────────────────────────────────────────────────────────────────
// Reusable "service unavailable" fallback for when the orchestrator circuit
// breaker is open or upstream is unreachable.  Renders inside the page
// layout (not full-screen) so nav/sidebar remain functional.
//
// Designed for pac-glass dark palette, WCAG AA contrast.
// ────────────────────────────────────────────────────────────────────────────

interface ServiceUnavailableProps {
  /** Header text — defaults to "Service temporarily unavailable" */
  title?: string;
  /** Extra context for the user */
  detail?: string;
  /** Retry callback — shows "Retry" button when provided */
  onRetry?: () => void;
  /** Whether a retry is currently in flight */
  isRetrying?: boolean;
}

export function ServiceUnavailable({
  title = 'Service temporarily unavailable',
  detail,
  onRetry,
  isRetrying,
}: ServiceUnavailableProps) {
  return (
    <div className="flex items-center justify-center py-16 px-4">
      <div className="pac-glass-raised rounded-2xl p-8 max-w-md text-center space-y-4 border border-neutral-700/50">
        {/* animated pulse ring */}
        <div className="mx-auto w-12 h-12 relative">
          <span className="absolute inset-0 rounded-full bg-amber-500/20 animate-ping" />
          <span className="relative flex items-center justify-center w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30">
            <svg className="w-6 h-6 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </span>
        </div>

        <p className="text-lg font-semibold text-white">{title}</p>

        <p className="text-sm text-neutral-400">
          {detail || 'The backend service is recovering. Data will appear automatically once connectivity is restored.'}
        </p>

        {onRetry && (
          <button
            onClick={onRetry}
            disabled={isRetrying}
            className="pac-glass-btn px-6 py-2 rounded-lg border border-amber-500/40
                       text-amber-400 hover:border-amber-400 transition
                       disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isRetrying ? 'Retrying…' : 'Retry now'}
          </button>
        )}

        <p className="text-xs text-neutral-600">
          Auto-retry is active — this page will recover on its own.
        </p>
      </div>
    </div>
  );
}
